import React, { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { motion } from "framer-motion";
import { Lock } from "lucide-react";
import { useNotification } from "../components/NotificationContext";
import { resetPassword } from "../services/userService";

const ResetPasswordPage = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const showNotification = useNotification();

  const token = useMemo(() => searchParams.get("token") || "", [searchParams]);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const [error, setError] = useState("");

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError("");
    setSuccessMessage("");

    if (!token) {
      const msg = "Geçersiz veya eksik şifre sıfırlama bağlantısı.";
      setError(msg);
      showNotification(msg, "error");
      return;
    }

    if (!password || !confirmPassword) {
      const msg = "Lütfen tüm alanları doldurun.";
      setError(msg);
      showNotification(msg, "error");
      return;
    }

    if (password.length < 6) {
      const msg = "Şifre en az 6 karakter olmalıdır.";
      setError(msg);
      showNotification(msg, "error");
      return;
    }

    if (password !== confirmPassword) {
      const msg = "Şifreler birbiriyle eşleşmiyor.";
      setError(msg);
      showNotification(msg, "error");
      return;
    }

    try {
      setLoading(true);

      const response = await resetPassword(token, password);

      const msg =
        response?.message || "Şifreniz başarıyla güncellendi.";

      setSuccessMessage(msg);
      showNotification(msg, "success");

      setPassword("");
      setConfirmPassword("");

      setTimeout(() => {
        navigate("/login");
      }, 1500);
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        "Şifre güncellenirken bir hata oluştu.";

      setError(msg);
      showNotification(msg, "error");
    } finally {
      setLoading(false);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.4 }}
      className="min-h-screen py-12 bg-secondary-light"
    >
      <div className="max-w-md container-custom">
        <div className="p-8 bg-white rounded-lg shadow-medium">
          <h1 className="mb-3 text-2xl font-bold text-gray-dark">
            Yeni Şifre Oluştur
          </h1>

          <p className="mb-6 text-gray">
            Hesabınız için yeni şifrenizi belirleyin.
          </p>

          {successMessage && (
            <div className="p-3 mb-4 text-green-600 rounded-md bg-green-50">
              {successMessage}
            </div>
          )}

          {error && (
            <div className="p-3 mb-4 text-red-600 rounded-md bg-red-50">
              {error}
            </div>
          )}

          {!token ? (
            <div className="p-3 text-red-600 rounded-md bg-red-50">
              Geçersiz veya eksik şifre sıfırlama bağlantısı.
            </div>
          ) : (
            <form onSubmit={handleResetPassword}>
              <div className="mb-4">
                <label htmlFor="password" className="block mb-1 text-gray-dark">
                  Yeni Şifre
                </label>
                <div className="relative">
                  <input
                    type="password"
                    id="password"
                    name="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="w-full py-2 pl-10 pr-4 border rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder="Yeni şifrenizi girin"
                    required
                  />
                  <Lock
                    className="absolute left-3 top-2.5 text-gray"
                    size={18}
                  />
                </div>
              </div>

              <div className="mb-6">
                <label
                  htmlFor="confirmPassword"
                  className="block mb-1 text-gray-dark"
                >
                  Yeni Şifre Tekrar
                </label>
                <div className="relative">
                  <input
                    type="password"
                    id="confirmPassword"
                    name="confirmPassword"
                    value={confirmPassword}
                    onChange={(e) => setConfirmPassword(e.target.value)}
                    className="w-full py-2 pl-10 pr-4 border rounded-md focus:outline-none focus:ring-1 focus:ring-primary"
                    placeholder="Şifrenizi tekrar girin"
                    required
                  />
                  <Lock
                    className="absolute left-3 top-2.5 text-gray"
                    size={18}
                  />
                </div>
              </div>

              <button
                type="submit"
                className="w-full mb-4 btn-primary"
                disabled={loading}
              >
                {loading ? "Şifre güncelleniyor..." : "Şifreyi Güncelle"}
              </button>
            </form>
          )}

          <p className="mt-2 text-center text-gray">
            <Link to="/login" className="text-primary hover:text-primary-dark">
              Giriş sayfasına dön
            </Link>
          </p>
        </div>
      </div>
    </motion.div>
  );
};

export default ResetPasswordPage;